const API_BASE = '/api';
const DEFAULT_TIMEOUT = 30000; // 30 seconds
const LONG_TIMEOUT = 120000; // 2 minutes for long operations

class APIClient {
  async request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;
    const timeout = options.timeout || DEFAULT_TIMEOUT;

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // Pull the JWT the AuthContext stores so authenticated endpoints (axiom,
    // personas, secrets…) resolve req.user instead of falling through optionalAuth.
    const authToken = typeof localStorage !== 'undefined' ? localStorage.getItem('etch.token') : null;

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        ...options.headers
      },
      ...options,
      signal: controller.signal
    };

    // Remove custom timeout property from config
    delete config.timeout;

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const body = await response.json().catch(() => ({ error: 'Request failed' }));
        const error = new Error(body.error || `HTTP ${response.status}`);
        // Callers need to tell "gone / not yours" apart from a real failure —
        // a conversation id left in localStorage by another account 404s and
        // should be dropped silently, not surfaced as an error.
        error.status = response.status;
        throw error;
      }

      return response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // Session endpoints
  async createSession(workspacePath, settings = {}) {
    const { provider, model, ollamaBaseUrl, mode, language } = settings;
    return this.request('/sessions', {
      method: 'POST',
      timeout: LONG_TIMEOUT, // Session creation can take time for large codebases
      body: JSON.stringify({
        workspacePath,
        provider,
        model,
        // Sent only when the user actually set one (SSRF hardening, nyxCore
        // action point f208bd62): an omitted key lets the server fall back to
        // the resolved user/tenant/env `ollama_url` setting. A hardcoded
        // default here would instead override that setting on every request,
        // for every provider — not just 'ollama' — since JSON.stringify keeps
        // an explicit `null`/`''`, unlike a genuinely absent field.
        ...(ollamaBaseUrl ? { ollamaBaseUrl } : {}),
        mode,
        language
      })
    });
  }

  async getSession(sessionId) {
    return this.request(`/sessions/${sessionId}`);
  }

  async deleteSession(sessionId) {
    return this.request(`/sessions/${sessionId}`, {
      method: 'DELETE'
    });
  }

  async clearHistory(sessionId) {
    return this.request(`/sessions/${sessionId}/clear`, {
      method: 'POST'
    });
  }

  // CKB endpoints (use longer timeout for codebase analysis)
  async getArchitecture(sessionId, options = {}) {
    const { depth = 2, granularity = 'directory', inferModules = true, includeMetrics = false, noCache = false } = options;
    const params = new URLSearchParams({
      sessionId,
      depth: String(depth),
      granularity,
      inferModules: String(inferModules),
      includeMetrics: String(includeMetrics)
    });
    if (noCache) {
      params.set('noCache', 'true');
    }
    return this.request(`/ckb/architecture?${params}`, {
      timeout: LONG_TIMEOUT
    });
  }

  async getConcepts(sessionId, limit = 12) {
    return this.request(`/ckb/concepts?sessionId=${sessionId}&limit=${limit}`, {
      timeout: LONG_TIMEOUT
    });
  }

  async searchSymbols(sessionId, query, limit = 20) {
    return this.request('/ckb/search', {
      method: 'POST',
      timeout: LONG_TIMEOUT,
      body: JSON.stringify({ sessionId, query, limit })
    });
  }

  async explore(sessionId, target, options = {}) {
    return this.request('/ckb/explore', {
      method: 'POST',
      timeout: LONG_TIMEOUT,
      body: JSON.stringify({ sessionId, target, ...options })
    });
  }

  async explainFile(sessionId, path) {
    return this.request('/ckb/file', {
      method: 'POST',
      timeout: LONG_TIMEOUT,
      body: JSON.stringify({ sessionId, path })
    });
  }

  async understand(sessionId, query, options = {}) {
    return this.request('/ckb/understand', {
      method: 'POST',
      timeout: LONG_TIMEOUT,
      body: JSON.stringify({ sessionId, query, ...options })
    });
  }

  async getCallGraph(sessionId, symbolId, options = {}) {
    return this.request('/ckb/callgraph', {
      method: 'POST',
      timeout: LONG_TIMEOUT,
      body: JSON.stringify({ sessionId, symbolId, ...options })
    });
  }

  async findReferences(sessionId, symbolId, options = {}) {
    return this.request('/ckb/references', {
      method: 'POST',
      timeout: LONG_TIMEOUT,
      body: JSON.stringify({ sessionId, symbolId, ...options })
    });
  }

  async prepareChange(sessionId, target, changeType = 'modify') {
    return this.request('/ckb/prepare-change', {
      method: 'POST',
      timeout: LONG_TIMEOUT,
      body: JSON.stringify({ sessionId, target, changeType })
    });
  }

  async traceUsage(sessionId, symbolId, options = {}) {
    return this.request('/ckb/trace-usage', {
      method: 'POST',
      timeout: LONG_TIMEOUT,
      body: JSON.stringify({ sessionId, symbolId, ...options })
    });
  }

  async getOnboardingPlan(sessionId) {
    return this.request(`/ckb/onboarding-plan?sessionId=${sessionId}`, {
      timeout: LONG_TIMEOUT
    });
  }

  // File tree endpoints
  async getFileTree(sessionId) {
    return this.request(`/sessions/${sessionId}/files`);
  }

  async getFileContent(sessionId, filePath) {
    return this.request(`/sessions/${sessionId}/files/${filePath}`);
  }

  // Settings endpoints
  async getProviders() {
    return this.request('/settings/providers');
  }

  // No client-side default: an omitted baseUrl lets the server resolve it
  // itself (tenant/user ollama_url, then OLLAMA_DEFAULT_BASE_URL — routes/
  // settings.js resolveOllamaBaseUrl()). A hardcoded 'http://localhost:11434'
  // here always shadowed that — a Docker self-host's own OLLAMA_BASE_URL or a
  // hosted tenant's configured Ollama could never be reached by this panel.
  async getOllamaModels(baseUrl) {
    return this.request(`/settings/ollama/models${baseUrl ? `?baseUrl=${encodeURIComponent(baseUrl)}` : ''}`);
  }

  async getOllamaAvailable() {
    return this.request('/settings/ollama/available');
  }

  async getOllamaStatus(baseUrl) {
    return this.request(`/settings/ollama/status${baseUrl ? `?baseUrl=${encodeURIComponent(baseUrl)}` : ''}`);
  }

  async pullOllamaModel(baseUrl, model, onProgress) {
    const url = `${API_BASE}/settings/ollama/pull`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ baseUrl, model })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to pull model' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    // Stream the response (newline-delimited JSON)
    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter(line => line.trim());

        for (const line of lines) {
          try {
            const data = JSON.parse(line);
            if (onProgress) {
              onProgress(data);
            }
            if (data.status === 'error') {
              throw new Error(data.error);
            }
          } catch (e) {
            if (e.message !== 'Unexpected end of JSON input') {
              throw e;
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  async deleteOllamaModel(baseUrl, modelName) {
    return this.request(`/settings/ollama/models/${encodeURIComponent(modelName)}${baseUrl ? `?baseUrl=${encodeURIComponent(baseUrl)}` : ''}`, {
      method: 'DELETE'
    });
  }

  // Persona Studio endpoints (nyxCore /api/v1/persona/*)
  async listPersonas({ force = false } = {}) {
    return this.request(`/personas${force ? '?force=1' : ''}`);
  }

  async personaChat({ circleId, personaId, messages, conversationId, externalUserId, model, temperature, maxTokens, useSkills } = {}) {
    return this.request('/personas/chat', {
      method: 'POST',
      timeout: 180_000,
      body: JSON.stringify({
        circleId,
        personaId,
        messages,
        conversationId,
        externalUserId,
        model,
        temperature,
        maxTokens,
        useSkills,
      }),
    });
  }

  // Conversation history (PGlite-backed)
  async listConversations({ workspace, search, limit = 200 } = {}) {
    const params = new URLSearchParams();
    if (workspace) params.set('workspace', workspace);
    if (search) params.set('search', search);
    params.set('limit', String(limit));
    return this.request(`/conversations?${params.toString()}`);
  }

  async getConversation(id) {
    return this.request(`/conversations/${encodeURIComponent(id)}`);
  }

  async createConversation({ workspacePath = null, mode = 'codebase', title = null, personaId = null, personaName = null } = {}) {
    return this.request('/conversations', {
      method: 'POST',
      body: JSON.stringify({ workspacePath, mode, title, personaId, personaName }),
    });
  }

  async appendConversationMessage(id, { role, content, metadata = null }) {
    return this.request(`/conversations/${encodeURIComponent(id)}/messages`, {
      method: 'POST',
      body: JSON.stringify({ role, content, metadata }),
    });
  }

  async renameConversation(id, title) {
    return this.request(`/conversations/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
  }

  async deleteConversation(id) {
    return this.request(`/conversations/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async autoTitleConversation(id, sessionId) {
    return this.request(`/conversations/${encodeURIComponent(id)}/auto-title`, {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  }

  // Health check
  async health() {
    return this.request('/health');
  }
}

export const apiClient = new APIClient();
export default apiClient;
